<template>
	<v-card color="rgba(41, 41, 47, 0.8)">
		<v-card-title>{{ $t("Updatecoordinatevalue") }}</v-card-title>
		<v-container>
			<v-row class="text-center">
				<template v-if="checkNEZ">
					<v-col cols="15" sm="4">
						<div>Y {{ $t("Surveysystem") }}({{ distanceLabel }})</div>
					</v-col>
					<v-col cols="15" sm="4">
						<div>X {{ $t("Surveysystem") }}({{ distanceLabel }})</div>
					</v-col>
				</template>
				<template v-else-if="!checkNEZ">
					<v-col cols="15" sm="4">
						<div>Y {{ $t("Surveysystem") }}({{ distanceLabel }})</div>
					</v-col>
					<v-col cols="15" sm="4">
						<div>X {{ $t("Surveysystem") }}({{ distanceLabel }})</div>
					</v-col>
				</template>
				<v-col cols="15" sm="4">
					<div>Z ({{ distanceLabel }})</div>
				</v-col>
			</v-row>
			<v-row class="text-center">
				<template v-if="checkNEZ">
					<v-col cols="15" sm="4">
						<DistanceSettingInput :model-value="y" :step="0.001" :decimal-length="3" variant="solo"
							class="mt-2" :disabled="disableY"
							@update:model-value="($event) => checkCoordinatesValidity($event, 'y')" />
					</v-col>
					<v-col cols="15" sm="4">
						<DistanceSettingInput :model-value="x" :step="0.001" :decimal-length="3" variant="solo"
							class="mt-2" :disabled="disableX"
							@update:model-value="($event) => checkCoordinatesValidity($event, 'x')" />
					</v-col>
				</template>
				<template v-else-if="!checkNEZ">
					<v-col cols="15" sm="4">
						<DistanceSettingInput :model-value="x" :step="0.001" :decimal-length="3" variant="solo"
							class="mt-2" :disabled="disableX"
							@update:model-value="($event) => checkCoordinatesValidity($event, 'x')" />
					</v-col>
					<v-col cols="15" sm="4">
						<DistanceSettingInput :model-value="y" :step="0.001" :decimal-length="3" variant="solo"
							class="mt-2" :disabled="disableY"
							@update:model-value="($event) => checkCoordinatesValidity($event, 'y')" />
					</v-col>
				</template>
				<v-col cols="15" sm="4">
					<DistanceSettingInput :model-value="z" :step="0.001" :decimal-length="3" variant="solo"
						class="mt-2" :disabled="disableZ"
						@update:model-value="($event) => checkCoordinatesValidity($event, 'z')" />
				</v-col>
			</v-row>
			<p v-if="!isUpdateValid.x || !isUpdateValid.y || !isUpdateValid.z" class="pl-4"
				style="color: #f44336; font-size: 14px">
				{{ $t("PLEASE_ENTER_VALID_INPUT") }}</p>
			<div class="d-flex text-center justify-space-around">
				<v-btn @click="confirmUpdate" class="px-9" color="primary"
					:disabled="!isUpdateValid.x || !isUpdateValid.y || !isUpdateValid.z">
					{{ $t("OK") }}
				</v-btn>
				<v-btn @click="cancelUpdate" class="" color="warning">
					{{ $t("CANCEL") }}
				</v-btn>
			</div>
		</v-container>
	</v-card>
</template>

<script lang="ts">
import { convertCoordinateToCesiumCrs } from "./../../utils/cesium-common";
import { ObjectType } from "@/models/objects/types";
import { useStore } from "vuex";
import { useUnitDisplay } from "@/composables/useUnitDisplay";
import DistanceSettingInput from "./DistanceSettingInput.vue";

export default {
	components: { DistanceSettingInput },
	setup() {
		const store = useStore();
		const { distanceLabel } = useUnitDisplay();
		return { store, distanceLabel };
	},
	name: "UpdateCtrlPointDialog",
	props: {
		id: String,
		pointId: String,
		entityUnderEdit: String,
		disabilityConditions: String,
		origin: String,
	},
	beforeMount() {
		this.checkNEZ = this.store.state.axis === "NEZ" ? true : false;
	},
	async mounted() {
		await this.initCoordinates();
	},
	data: () => ({
		x: 0,
		y: 0,
		z: 0,
		// prevPosition: null,
		isUpdateValid: { x: true, y: true, z: true },
		checkNEZ: null,
		disableX: false,
		disableY: false,
		disableZ: false,
	}),
	methods: {
		async initCoordinates() {
			this.x = 0;
			this.y = 0;
			this.z = 0;
			this.isUpdateValid = { x: true, y: true, z: true };
			this.$nextTick(async () => {
				// placement of Y and X text boxes
				this.checkNEZ = this.store.state.axis === "NEZ" ? true : false;

				// disability conditions
				if (this.disabilityConditions === "Z") {
					this.disableX = false;
					this.disableY = false;
					this.disableZ = true;
				} else if (this.disabilityConditions === "XY") {
					this.disableX = true;
					this.disableY = true;
					this.disableZ = false;
				} else if (this.disabilityConditions === "NONE") {
					this.disableX = false;
					this.disableY = false;
					this.disableZ = false;
				}

				if (!this.pointId) return;

				let pos = undefined;

				if (this.origin === "CesiumView") {
					// function to get coordinates of point
					if (this.entityUnderEdit === ObjectType.ROAD) {
						pos = this.store.getters.viewer.getCtrlPointCordiOnRoad(
							this.id,
							this.pointId,
						);
					}

					if (this.entityUnderEdit === ObjectType.FLAT_GROUND) {
						pos = this.store.getters.viewer.getCtrlPointCordiOnFlatGround(
							this.id,
							this.pointId,
						);
					}

					if (this.entityUnderEdit === ObjectType.TRENCH) {
						pos = this.store.getters.viewer.getCtrlPointCordiOnTrench(
							this.id,
							this.pointId,
						);
					}
				} else if (this.origin === "AltitudeView") {
					if (this.entityUnderEdit === ObjectType.ROAD) {
						const res = this.store.getters.viewer.getSlopeBreakPointInfoOnRoad(
							this.id,
							Number.parseInt(this.pointId),
						);
						pos = res.point;
					}
				}

				if (!pos) {
					return;
				}

				this.x = pos.x;
				this.y = pos.y;
				this.z = pos.z;
			});
		},
		checkCoordinatesValidity(inputValue, prop) {
			const isValid = Number.isFinite(inputValue);
			this.isUpdateValid[prop] = isValid;
			if (isValid) {
				this[prop] = inputValue;
			}
		},
		cancelUpdate() {
			// This snippet is not required as now control point is updated on confirm click.
			// if (this.prevPosition) {
			//   this.entityUnderEdit === ObjectType.ROAD
			//     ? window["viewer"].updateCtrlPointCordiOnRoad(this.id, this.pointId, this.prevPosition.x, this.prevPosition.y, this.prevPosition.z)
			//     : window["viewer"].updateCtrlPointCordiOnFlatGround(this.id, this.pointId, this.prevPosition.x, this.prevPosition.y, this.prevPosition.z);
			// }

			this.$emit("close");
		},
		async confirmUpdate() {
			if (
				this.isUpdateValid.x &&
				this.isUpdateValid.y &&
				this.isUpdateValid.z &&
				this.x !== undefined &&
				this.y !== undefined &&
				this.z !== undefined
			) {
				// ローディング開始
				await this.store.dispatch("executeWithSpinner", async () => {
					try {
						const response = await convertCoordinateToCesiumCrs({
							x: this.x,
							y: this.y,
							z: this.z,
						});
						const outX = Number(response.x);
						const outY = Number(response.y);
						const outZ = Number(response.z);
						if (isNaN(outX) || isNaN(outY) || isNaN(outZ)) {
							throw new Error(
								`Input Point Could not be transformed into respective wgs84 points`,
							);
						}

						if (this.origin === "CesiumView") {
							// function to update coordinates this takes coordinates for cesium crs.
							if (this.entityUnderEdit === ObjectType.ROAD) {
								this.store.getters.viewer.updateCtrlPointCordiOnRoad(
									this.id,
									this.pointId,
									outX,
									outY,
									outZ,
								);
							}

							if (this.entityUnderEdit === ObjectType.FLAT_GROUND) {
								if (this.disabilityConditions === "XY") {
									await this.store.getters.viewer.setFlatGroundPointsUniformHeight(
										this.id,
										this.z,
									);
								} else if (this.disabilityConditions === "NONE") {
									await this.store.getters.viewer.updateCtrlPointCordiOnFlatGround(
										this.id,
										this.pointId,
										outX,
										outY,
										outZ,
									);
								}
							}

							if (this.entityUnderEdit === ObjectType.TRENCH) {
								this.store.getters.viewer.updateCtrlPointCordiOnTrench(
									this.id,
									this.pointId,
									outX,
									outY,
									outZ,
								);
							}
						} else if (this.origin === "AltitudeView") {
							if (this.entityUnderEdit === ObjectType.ROAD) {
								const slopeBreaks = this.store.getters.viewer.getSlopeBreaks(
									this.id,
								);
								slopeBreaks[Number.parseInt(this.pointId)].height = this.z;
								this.store.getters.viewer.updateSlopeBreaksFromLongitudinalView(
									this.id,
									slopeBreaks,
								);
							}
						}
					} catch (e) {
						console.error(e);
					}
				});
			}
			this.$emit("close", false);
		},
	},
};
</script>
<style scoped></style>
